import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";

import CssBaseline from "@mui/joy/CssBaseline";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Dropdown,
  Input,
  IconButton,
  Textarea,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Menu,
  MenuItem,
  MenuButton,
  Modal,
  ModalClose,
  Sheet,
  Typography,
  Tooltip,
} from "@mui/joy";

import { CssVarsProvider, extendTheme } from "@mui/joy/styles";

import {
  createBrowserRouter,
  RouterProvider,
  Link,
  Outlet,
  useParams,
  useNavigate,
} from "react-router-dom";

import { f_date } from "./Sidebar";

import Still1 from "./img/ABunchOfQuestionsWithNoAnswers_Still1.jpeg";
import Still2 from "./img/ABunchOfQuestionsWithNoAnswers_Still2.jpeg";

import "@fontsource/ibm-plex-sans";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const theme = extendTheme({
  fontFamily: {
    display: "IBM Plex Sans", // applies to `h1`–`h4`
    body: "IBM Plex Sans", // applies to `title-*` and `body-*`
  },
  colorSchemes: {
    light: {
      palette: {},
    },
    dark: {
      palette: {},
    },
  },
});

const TitleAndAction = ({ title, Action }) => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          {title}
        </Typography>
        {Action}
      </Box>
    </>
  );
};

const f_num = (n) => {
  let s = "";

  const ns = "" + n;

  for (let i = 0; i < ns.length; i++) {
    s = ns[ns.length - i - 1] + s;
    if ((i + 1) % 3 === 0 && i < ns.length - 1) {
      s = "," + s;
    }
  }

  return s;
};

const QuestionBackground = ({ uid, spec }) => {
  if (!uid) {
    return "";
  }
  return (
    <Box
      sx={{
        position: "fixed",
        top: "20px",
        right: "20px",
        pointerEvents: "none",
        opacity: 0.5,
        textAlign: "right",
        width: "calc(100vw-140px)",
      }}
    >
      {spec
        .filter((x) => x.uid === uid)
        .map(({ questions, summaries, duration }) => (
          <>
            {summaries.map((x) => (
              <Typography>{x}</Typography>
            ))}
          </>
        ))}
    </Box>
  );
};

const Home = () => {
  const [ren, setRen] = useState([]);
  const [hov, setHov] = useState();

  useEffect(() => {
    const fn = async () => {
      const res = await (await fetch("/render-list")).json();
      res.reverse();
      setRen(res);
    };
    fn();
  }, []);

  let tot_duration = 0;
  let tot_questions = 0;
  for (const { questions, duration } of ren) {
    tot_questions += questions;
    tot_duration += duration;
  }

  return (
    <>
      <Button disabled={!ren.length} sx={{ m: 4 }} component={Link} to={`/all`}>
        {!ren.length
          ? "Loading..."
          : `${f_num(tot_questions)} Questions (${f_time(tot_duration)})`}
      </Button>

      <QuestionBackground uid={hov} spec={ren} />

      <List>
        {ren.map(({ uid, duration, questions, summaries }) => (
          <ListItem
            onMouseOver={() => setHov(uid)}
            onMouseOut={() => setHov()}
            component={Link}
            to={`/day/${uid}`}
          >
            {f_date(uid)}
          </ListItem>
        ))}
      </List>
    </>
  );
};

const RenderButton = ({ spoxId, sen, checked }) => {
  const [renderOpen, setRenderOpen] = useState(false);

  return (
    <>
      <Modal
        sx={{
          zIndex: 15000,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        open={renderOpen}
        onClose={() => setRenderOpen(false)}
      >
        <Sheet
          variant="outlined"
          sx={{
            maxWidth: 800,
            borderRadius: "md",
            p: 3,
            boxShadow: "lg",
          }}
        >
          <ModalClose variant="plain" sx={{ m: 1 }} />
          <Typography
            component="h2"
            id="modal-title"
            level="h4"
            textColor="inherit"
            fontWeight="lg"
            mb={1}
          >
            render
          </Typography>

          <LinearProgress />
          <Typography>Note: no completion indication yet...</Typography>
        </Sheet>
      </Modal>

      <Button
        color="primary"
        startDecorator={<CloudUploadIcon />}
        size="sm"
        onClick={() => {
          setRenderOpen(true);

          // start rendering!
          const fn = async () => {
            // Generate seq

            const seq = [];
            let lastIdx = -2;
            for (const [idx, dist] of Object.entries(sen)) {
              if (!checked[idx]) {
                continue;
              }
              if (lastIdx + 1 === Number(idx)) {
                // Merge
                seq[seq.length - 1].end = sen[idx].end;
              } else {
                // New!
                seq.push({
                  id: spoxId,
                  start: sen[idx].start,
                  end: sen[idx].end,
                });
              }
            }

            const rres = await fetch("/render", {
              method: "post",
              body: JSON.stringify({
                uid: `v1-${spoxId}`,
                spec: {
                  size: [640, 360],
                  seq: seq,
                },
              }),
            });
            const rout = await rres.json();
          };
          fn();
        }}
      >
        render
      </Button>
    </>
  );
};

function zp(n, len) {
  let s = "" + n;
  while (s.length < len) {
    s = "0" + s;
  }
  return s;
}
function f_time(t) {
  const h = Math.floor(t / (60 * 60));
  const m = Math.floor((t - h * 60 * 60) / 60);
  const s = Math.round(t % 60);

  return `${zp(h, 2)}:${zp(m, 2)}:${zp(s, 2)}`;
}

const Spox = () => {
  const { spoxId } = useParams();
  const [sen, setSen] = useState();
  const [checked, setChecked] = useState({});

  const duration = useMemo(() => {
    let tot = 0;
    for (let i = 0; i < (sen || []).length; i++) {
      if (checked[i]) {
        tot += sen[i].end - sen[i].start;
      }
    }
    return tot;
  }, [sen, checked]);

  useEffect(() => {
    setSen();

    const fn = async () => {
      const res = await fetch("/sentences", {
        method: "post",
        body: JSON.stringify({ uid: spoxId }),
      });
      const out = await res.json();
      setSen(out);

      const sel = {};
      for (let i = 0; i < out.length; i++) {
        sel[i] = out[i].checked;
      }
      setChecked(sel);
    };

    fn();
  }, [spoxId]);

  const Tx = (
    <Container>
      <Typography>{f_time(duration)} selected</Typography>

      {(sen || []).map(({ wdlist, start, run_idx, rep_idx }, idx) => (
        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          <Dropdown>
            <MenuButton slots={{ root: IconButton }}>
              <MoreVertIcon />
            </MenuButton>
            <Menu placement="bottom-start">
              <MenuItem
                onClick={() => {
                  console.log("Remove...");
                  const fn = async () => {
                    const res = await fetch("/sentences", {
                      method: "post",
                      body: JSON.stringify({
                        uid: spoxId,
                        idx,
                        task: "similar-voice",
                      }),
                    });
                    const matches = await res.json();
                    const checks = { ...checked };

                    matches.forEach((m, idx) => {
                      if (m) {
                        checks[idx] = false;
                      }
                    });

                    setChecked(checks);
                  };

                  fn();
                }}
              >
                remove voice
              </MenuItem>
              <MenuItem
                color="warning"
                onClick={() => {
                  console.log("Label...");

                  const name = prompt("name this voice");

                  const fn = async (name) => {
                    const res = await fetch("/sentences", {
                      method: "post",
                      body: JSON.stringify({
                        uid: spoxId,
                        idx,
                        name,
                        task: "ban-voice",
                      }),
                    });
                    const out = await res.json();
                    console.log("BANNED", out);
                  };

                  if (name) {
                    fn(name);
                  } else {
                    alert("no name provided - canceling");
                  }
                }}
              >
                ban voice
              </MenuItem>
              <MenuItem
                onClick={() => {
                  console.log("Label...");

                  const name = prompt("name this voice");

                  const fn = async (name) => {
                    const res = await fetch("/sentences", {
                      method: "post",
                      body: JSON.stringify({
                        uid: spoxId,
                        idx,
                        name,
                        task: "label-voice",
                      }),
                    });
                    const out = await res.json();
                    console.log("Labeled", out);
                  };

                  if (name) {
                    fn(name);
                  } else {
                    alert("no name provided - canceling");
                  }
                }}
              >
                name voice
              </MenuItem>
            </Menu>
          </Dropdown>
          <Checkbox
            checked={checked[idx]}
            onChange={(e) => {
              const obj = { ...checked };
              obj[idx] = e.target.checked;
              setChecked(obj);
            }}
            sx={{ flexGrow: 1 }}
            label={
              wdlist.map((x) => x.word).join("") +
              ` [Run ${run_idx} / Rep ${rep_idx}]`
            }
          />
        </Box>
      ))}
    </Container>
  );

  return (
    <>
      <TitleAndAction
        title={f_date(spoxId)}
        Action={<RenderButton spoxId={spoxId} sen={sen} checked={checked} />}
      />
      {!sen ? <i>Loading...</i> : Tx}
    </>
  );
};
const Render = () => {
  const { spoxId } = useParams();

  return (
    <>
      <Box p={4}>
        <Typography>
          Daily briefings are no longer viewable on this site.
        </Typography>
      </Box>
    </>
  );
};

const Main = () => {
  return (
    <>
      <Container sx={{ py: 4 }}>
        <Outlet />
      </Container>

      <hr />
      <Typography sx={{ mt: 2 }}>
        A Bunch of Questions With No Answers is a project of{" "}
        <a
          href="http://www.alexreynolds.net/"
          target="_blank"
          rel="noreferrer"
        >
          Alex Reynolds
        </a>{" "}
        and{" "}
        <a href="https://rmozone.com" target="_blank" rel="noreferrer">
          Robert M Ochshorn
        </a>
      </Typography>
    </>
  );
};

const Docs = () => {
  return <h2>docs</h2>;
};

const Voice = () => {
  return <h2>voice</h2>;
};

const AllQuestions = ({ uid }) => {
  const [vid, setVid] = useState();
  const [tx, setTx] = useState();

  useEffect(() => {
    const fn = async () => {
      const vres = await fetch("/signed-video", {
        method: "post",
        body: JSON.stringify({ uid }),
      });
      const vout = await vres.json();
      setVid(vout);

      const res = await fetch("/render-tx", {
        method: "post",
        body: JSON.stringify({ uid }),
      });
      const r_tx = await res.json();
      setTx(r_tx);
    };

    fn();
  }, []);

  if (!tx) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <>
      <Typography>{uid.split("-")[1]}</Typography>
      <video src={vid} controls width="100%" />
      {tx.seq.map(({ id, text, start, end }) => (
        <Typography>
          {id} ({f_time(start)} - {f_time(end)}): {text}
        </Typography>
      ))}
    </>
  );
};

const All = () => {
  const [vid, setVid] = useState();
  const [ren, setRen] = useState([]);

  useEffect(() => {
    const fn = async () => {
      const res = await (await fetch("/render-list")).json();
      setRen(res);
    };
    fn();
  }, []);

  useEffect(() => {
    const fn = async () => {
      const vres = await fetch("/signed-video", {
        method: "post",
        body: JSON.stringify({ all: true }),
      });
      const vout = await vres.json();
      setVid(vout);
    };

    fn();
  }, []);

  return (
    <>
      {!vid ? <i>Loading...</i> : <video src={vid} controls width="100%" />}

      <Box p={4}>
        {ren.map(({ summaries }) =>
          summaries.map((x) => <Typography>{x}</Typography>),
        )}
      </Box>
    </>
  );
};

const Place = ({ name, date, extlink }) => {
  return (
    <>
      <Typography sx={{ my: 2 }}>
        <b>{name}</b>, {date}
        {!extlink ? (
          ""
        ) : (
          <>
            {" "}
            (
            <a href={extlink} target="_blank">
              link
            </a>
            )
          </>
        )}
      </Typography>
    </>
  );
};

const Press = ({ name, author, extlink, publication }) => {
  return (
    <Typography sx={{ my: 2 }}>
      <i>{name}</i>
      {publication ? `, in ${publication}` : ""}, by {author}
      {!extlink ? (
        ""
      ) : (
        <>
          {" "}
          (
          <a href={extlink} target="_blank">
            link
          </a>
          )
        </>
      )}
    </Typography>
  );
};

const Info = () => {
  return (
    <>
      <Container sx={{ mb: 4 }}>
        <Typography level="h2">
          <b>A Bunch of Questions with No Answers, 2025</b>
        </Typography>
        <Typography>
          <i>HD video, 22 hours 52 minutes</i>
        </Typography>
        <Typography>
          A Bunch of Questions with No Answers is a record of the questions
          posed by journalists to the US State Department during press briefings
          between Oct 3, 2023 and the end of the Biden administration about the
          ongoing genocide in Palestine. As the endless demands for
          clarification and accountability were repeatedly met with convoluted
          evasions, the artists chose to edit them out.
        </Typography>
      </Container>

      <Box sx={{ display: "flex", width: "100%" }}>
        <img style={{ width: "50%", height: "auto" }} src={Still1} />
        <img style={{ width: "50%", height: "auto" }} src={Still2} />
      </Box>

      <Container sx={{ mb: 4, mt: 4 }}>
        <Typography level="h3" sx={{ mt: 2 }}>
          Press and Screenings
        </Typography>

        <Place
            name="Berlin Atonal, The Infinite Now, Berlin, Germany"
            date="May 16 - 17 2026"
          extlink="https://berlin-atonal.com/"
        />

          <Divider />

        <Place
            name="La Virreina Centre de la Image, Barcelona, Spain"
            date="March 28 - June 28 2026"
          extlink="https://ajuntament.barcelona.cat/lavirreina/en/exhibitions/bunch-questions-no-answers/1002"
        />


          <Divider />

        <Press
          name="The Defining Artworks of 2025 (#11)"
          publication="ARTnews"
          author="ARTnews"
          extlink="https://www.artnews.com/list/art-news/news/most-important-artworks-1234762369/adrien-brody-my-marilyn/"
        />

        <Press
          name="The Surprising—and Welcome—Comeback of Cinema Vérité"
          author="Emily Watlington"
          publication="Art in America"
          extlink="https://www.artnews.com/art-in-america/columns/cinema-verite-robert-ochshorn-alex-reynolds-sharon-hayes-jill-frank-1234764606/"
        />

        <Divider />

        <Place
          name="Salt Beyoğlu, Istanbul, Türkiye"
          date="November 29-30, 2025"
          extlink="https://saltonline.org/en/2968/gosterim-a-bunch-of-questions-with-no-answers-cevaplanmamis-bir-dizi-soru"
        />

        <Divider />

        <Place
            name="Rochester Contemporary Art Center, USA"
          date="November 20-23, 2025"
          extlink="https://www.rochestercontemporary.org/exhibitions/a-bunch-of-questions-with-no-answers/"
        />

        <Divider />

        <Place
          name="Belfast Film Festival, North of Ireland"
          date="November 1-6, 2025"
          extlink="https://belfastfilmfestival.org/whats-on"
        />

        <Divider />

        <Place
          name="LUCA School of Arts Sint-Lukas Brussel, Belgium"
          date="October 20-31, 2025"
          extlink="https://www.luca-arts.be/en/bunch-questions-no-answers"
        />

        <Divider />

        <Place
          name="SculptureCenter, NYC, USA"
          date="September 18 to October 12, 2025"
          extlink="https://www.sculpture-center.org/exhibitions/14595/a-bunch-of-questions-with-no-answers"
        />

        <Divider />

        <Place
          name="KANAL, MolenFest, Brussels, Belgium"
          date="September 5-6 2025"
          extlink="https://kanal.brussels/en/calendar/bunch-questions-no-answers"
        />

        <Divider />

        <Press
          name="In a 23-Hour Film, Journalists Pose Unanswered Questions to the US State Department About Palestine"
          author="Emily Watlington"
          publication="Art in America"
          extlink="https://www.artnews.com/art-in-america/aia-reviews/hour-film-journalists-palestine-state-department-alex-reynolds-robert-ochshorn-1234750308/"
        />

        <Divider />

        <Place
          name="Otherfield Festival, UK"
          date="August 1-3 2025"
          extlink="https://www.otherfield.uk/programme-event/a-bunch-of-questions-with-no-answers-3"
        />

        <Divider />

        <Press
          name="23 Hours Inside State Dept. Press Briefings on the Gaza Genocide"
          author="Malu Halasa"
          publication="The Markaz Review"
          extlink="https://themarkaz.org/23-hours-inside-state-dept-press-briefings-on-the-gaza-genocide/"
        />

        <Divider />

        <Place
          name="Et al., San Francisco, USA"
          date="May 18, 25, June 1, 2025"
          extlink={"https://etaletc.com/a-bunch-of-questions-with-no-answers"}
        />

        <Press
          name="7 Must-See Museum and Gallery Shows This Summer"
          author="Sarah Hotchkiss"
          publication="KQED"
          extlink="https://www.kqed.org/arts/13976229/visual-art-summer-2025-guide-museums-galleries-shows"
        />

        <Divider />

        <Place
          name="Courtisane, Ghent, Belgium"
          date="April 4-6, 2025"
          extlink={
            "https://www.courtisane.be/en/event/undercurrents-13-a-bunch-of-questions-with-no-answers"
          }
        />

        <Press
          name="Courtisane: zachtjes ondergraven"
          author="Kathy Vanhout"
          extlink="https://www.fantomas.be/artikel/courtisane-zachtjes-ondergraven/"
        />

        <Divider />

        <Place name="ARCO, Madrid, Spain" date="March 5-9, 2024" />

        <Press
          name="ARCO convierte la crispación política en arte: la ultraderecha metida en un lavavajillas y una cadena con los muertos en las residencias de Madrid"
          author="Pablo R. Roces"
          extlink="https://www.elmundo.es/cultura/2025/03/05/67c75c7fe85ece5e438b4584.html"
        />

        <Press
          name="Arco 2025: del lavavajillas donde se lava la cara Trump a la acuarela en la que llueve de verdad"
          author="Teresa Sesé"
          extlink="https://www.lavanguardia.com/cultura/20250305/10449106/arco-lavavajillas-lava-cara-trump-acuarela-llueve-verdad.html"
        />

        <Press
          name="Arco viu en un altre planeta"
          author="Teresa Sesé"
          extlink="https://www.lavanguardia.com/encatala/20250306/10449970/arco-viu-altre-planeta.html"
        />

        <Press
          name="Todo lo que debes saber sobre ARCO 2025: Las galerías españolas en el programa general"
          author="María Marco"
          extlink="https://www.elespanol.com/el-cultural/arte/20250303/debes-saber-arco-galerias-espanolas-programa-general/926657852_0.html"
        />

        <Press
          name="ARCO continúa abonada a la política"
          author="Javier Ors & Julián Herrero"
          extlink="https://www.larazon.es/cultura/arco-continua-abonada-politica/20250305/1169037.html"
        />

        <Press
          name="Arco 2025: Trump en un lavavajillas"
          author="Antoni Ribas Tur"
          extlink="https://es.ara.cat/cultura/arte/trump-lavavajillas-arte-politico-excepcion-nueva-edicion-arco_1_5305859.html"
        />

        <Divider />

        <Place
          name="misted.cc"
          date="October 2 - November 1, 2024"
          extlink="https://misted.cc/text/a-bunch-of-questions-with-no-answers-transcribed"
        />

        <Divider />

        <Press
          name="Non-Fiction 07: The Elephant"
          author="Elhum Shakerifar"
          extlink="https://opencitylondon.com/non-fiction/the-elephant/introduction/"
        />

        <Typography level="h3" sx={{ mt: 2 }}>
          Bios
        </Typography>

        <Typography>
          <b>Robert M Ochshorn</b> is a software engineer, cultural theorist,
          and media researcher based in Brussels and New York City. He develops
          unusual digital interfaces to observe and activate sound, video,
          touch, and language. He is interested in how new communication tools
          enable new social practices, and vice versa. Ochshorn is co-founder
          and CEO at Reduct.Video.
        </Typography>
        <Typography>
          <b>Alex Reynolds</b> delves into our modes of relation as they appear
          embodied in cinematic language, questioning the medium’s conventions
          to explore the emancipatory potential of play and refusal. <br />
          Her work has been shown at Contour Biennial (Mechelen), Guggenheim
          Bilbao, Beursschouwburg (Brussels), Index Foundation (Stockholm),
          Hollybush Gardens (London), Caixaforum Barcelona, Ca2m (Madrid) or the
          Miró Foundation (Barcelona), among others. Her films have been
          screened at festivals such as BFI London Film Festival, FIDMarseille,
          Prismatic Ground (New York), Courtisane (Ghent) or Documenta Madrid.
          She is visiting professor at The Royal Academy of Fine Arts (KASK) in
          Ghent.
        </Typography>
      </Container>
    </>
  );
};

const MailingList = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  if (sent) {
    return (
      <Box p={4}>
        <Typography>Thanks!</Typography>
      </Box>
    );
  }

  return (
    <>
      <Typography level="h3" sx={{ mt: 2 }}>
        Get updates from the project
      </Typography>
      <Box p={2}>
        <Input
          disabled={loading}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
        />
      </Box>
      <Button
        disabled={
          loading ||
          !(email && email.indexOf("@") > 0 && email.indexOf(".") > 1)
        }
        onClick={() => {
          const fn = async () => {
            setLoading(true);
            await fetch("/register", {
              method: "post",
              body: JSON.stringify({ email }),
            });
            setSent(true);
            setLoading(false);
          };

          fn();
        }}
      >
        {loading ? "Loading..." : "Submit"}
      </Button>
    </>
  );
};

const CompareVideos = ({ video_id }) => {
  const uid = video_id || "may-2-2024";

  const [gentleVid, setGentleVid] = useState();
  const [roughVid, setRoughVid] = useState();

  const [show, setShow] = useState(false);

  useEffect(() => {
    const fn = async () => {
      let vres = await fetch("/signed-video", {
        method: "post",
        body: JSON.stringify({ uid }),
      });
      let vout = await vres.json();
      setRoughVid(vout);

      vres = await fetch("/signed-video", {
        method: "post",
        body: JSON.stringify({ uid, official: true }),
      });
      vout = await vres.json();
      setGentleVid(vout);
    };

    if (show) {
      fn();
    }
  }, [show, uid]);

  if (!show) {
    return (
      <div style={{ textAlign: "center" }}>
        <button disabled={show} onClick={() => setShow(true)}>
          {show ? "loading..." : "compare video"}
        </button>
      </div>
    );
  }

  if (!gentleVid || !roughVid) {
    return <i>Loading...</i>;
  }

  return (
    <table border={1}>
      <tr>
        <th style={{ width: "50%" }}>old render</th>
        <th style={{ width: "50%" }}>new render</th>
      </tr>
      <tr>
        <td>
          <video src={roughVid} controls width="100%" />
        </td>
        <td>
          <video src={gentleVid} controls width="100%" />
        </td>
      </tr>
    </table>
  );
};

const CompareTx = () => {
  const uid = "may-2-2024";

  const [roughTx, setRoughTx] = useState();
  const [gentleTx, setGentleTx] = useState();

  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!show) return;

    setRoughTx();
    setGentleTx();

    const fn = async () => {
      setGentleTx(await (await fetch(`/gentle-tx?uid=${uid}`)).json());
      setRoughTx(await (await fetch(`/rough-tx?uid=${uid}`)).json());
    };

    fn();
  }, [uid, show]);

  const zipped = useMemo(() => {
    let out = [];

    if (!roughTx || !gentleTx) return [];

    let gentleSegIdx = 0;

    //use roughTx as master
    for (const roughSeg of roughTx) {
      const row = {
        start: roughSeg.start,
        end: roughSeg.end,
        roughText: roughSeg.wdlist.map((x) => x.word).join(""),
        checked: roughSeg.checked,

        gentleText: "",
        gentleSpeaker: "",
        gentleStart: null,
        gentleEnd: null,
        gentleChecked: null,
      };

      out.push(row);

      while (true) {
        const gentleSeg = gentleTx[gentleSegIdx];
        if (!gentleSeg) {
          break;
        }

        const aligned = gentleSeg.wdlist.filter((x) => x.end);
        if (!aligned.length) {
          gentleSegIdx += 1;
          continue;
        }

        const gentleStart = aligned[0].start;
        const gentleEnd = aligned[aligned.length - 1].end;

        const gentleChecked = gentleSeg.checked;

        if ((row.start + row.end) / 2 < gentleStart) {
          // roughTx comes first -- nothing to do!

          break;
        }

        const gentleObj = {
          gentleText: gentleSeg.wdlist.map((x) => x.word).join(""),
          gentleSpeaker: gentleSeg.speaker,
          gentleStart,
          gentleEnd,
          gentleChecked,
        };

        if ((row.start + row.end) / 2 > gentleEnd) {
          // gentle row comes after this seg! slip this one in and continue

          out = [
            ...out.slice(0, out.length - 1),
            {
              start: null,
              end: null,
              roughText: "",
              checked: false,

              ...gentleObj,
            },
            row,
          ];
        } else {
          // match! merge & increment & break

          out[out.length - 1] = { ...row, ...gentleObj };

          gentleSegIdx += 1;
          break;
        }

        gentleSegIdx += 1;
      }
    }

    return out;
  }, [gentleTx, roughTx]);

  if (!show || !roughTx || !gentleTx) {
    return (
      <div style={{ textAlign: "center" }}>
        <button disabled={show} onClick={() => setShow(true)}>
          {show ? "loading..." : "compare transcripts"}
        </button>
      </div>
    );
  }

  return (
    <table border={1}>
      <tr>
        <th>old in?</th>
        <th style={{ width: "30%" }}>our tx</th>
        <th>new in?</th>
        <th style={{ width: "30%" }}>official tx</th>
        <th>official speaker</th>
        <th>old timing</th>
        <th>new timing</th>
      </tr>
      {zipped.map(
        ({
          roughText,
          gentleText,
          checked,
          gentleChecked,
          gentleSpeaker,
          gentleStart,
          gentleEnd,
          start,
          end,
        }) => (
          <tr>
            <td>{checked ? "Y" : "N"}</td>
            <td style={{ color: checked ? "magenta" : undefined }}>
              {roughText}
            </td>
            <td>{gentleChecked ? "Y" : "N"}</td>
            <td style={{ color: gentleChecked ? "magenta" : undefined }}>
              {gentleText}
            </td>
            <td>{gentleSpeaker || ""}</td>
            <td>
              {start} - {end}
            </td>
            <td>
              {gentleStart} - {gentleEnd}
            </td>
          </tr>
        ),
      )}
    </table>
  );
};

const OfficialTranscripts = () => {
  return (
    <>
      <div style={{ maxWidth: 800, margin: "auto", padding: 20 }}>
        <p style={{ textAlign: "right" }}>2025-02-19</p>
        <p>two updates:</p>
        <ol>
          <li>
            I've set up the rendering to crop out the accessibility frame; and
          </li>
          <li>
            I've changed the timing to expand into the first/last syllable of
            the spokesperson.
          </li>
        </ol>
      </div>
      <CompareVideos video_id="january-15-2025" />
      <div style={{ maxWidth: 800, margin: "auto", padding: 20 }}>
        <p>
          <i>-rmo</i>
        </p>
      </div>
      <hr />

      <div style={{ maxWidth: 800, margin: "auto", padding: 20 }}>
        <p style={{ textAlign: "right" }}>2025-02-18</p>
        <p>dear alex,</p>
        <p>
          it's taken the past two days to get this round of development together
          as something you can see... excepting that to the best of my knowledge
          there simply{" "}
          <a href="https://2021-2025.state.gov/department-press-briefings/page/28/">
            isn't
          </a>{" "}
          an official transcript for november 8, 2023 (even though there is{" "}
          <a href="https://video.state.gov/detail/videos/top-stories/video/6340745531112/department-of-state-daily-press-briefing---november-8-2023?autoStart=true&sort_by=PUBLISH_DATE%3ADESC">
            a video
          </a>
          ), the other 153 transcripts have been imported from the state
          department and linked to the video.
        </p>
        <p>
          i made a preliminary edit with the new timing for may 2, 2024 (& can
          easily run for any other day). this also shows what the hd renders
          look like. but mostly, i'm curious for your sense of "feel" around the
          cuts... there's a bit more room to tweak timing if some seem off.
          there's also a transcript comparison table, though it's a bit
          confusing...
        </p>
      </div>

      <CompareVideos />
      <CompareTx />

      <div style={{ maxWidth: 800, margin: "auto", padding: 20 }}>
        <p>
          lots more to do... let's touch base when you get this on next steps?
        </p>

        <p>
          for now...,
          <br />
          <i>-r.m.o.</i>
        </p>
      </div>
    </>
  );
};

const Debug = () => {
  return (
    <div id="main">
      <OfficialTranscripts />
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Main />,
    children: [
      { path: "", element: <Info /> },
      { path: "listing", element: <Home /> },
      { path: "edit/:spoxId", element: <Spox /> },
      { path: "day/:spoxId", element: <Render /> },
      { path: "voice/:spoxId", element: <Voice /> },
      { path: "all", element: <All /> },
      { path: "all-questions", element: <AllQuestions uid="render-all" /> },
      { path: "time", element: <AllQuestions uid="render-time" /> },
      { path: "said", element: <AllQuestions uid="render-said" /> },
      { path: "documentation", element: <Docs /> },
      { path: "debug", element: <Debug /> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <CssVarsProvider
      defaultMode="system"
      theme={theme}
      modeStorageKey="diary-system-mode"
      disableNestedContext
    >
      <CssBaseline />
      <RouterProvider router={router} />
    </CssVarsProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
